import { Component, OnInit, OnDestroy } from '@angular/core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { FilterService } from '../../core/filter/filter.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { State } from '@progress/kendo-data-query';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { EventListService } from 'src/app/core/service/grid-data/grid-data-event.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { EventDetails } from 'src/app/event/model/event';
import { IntlService } from '@progress/kendo-angular-intl';

@Component({
  standalone: false,
  selector: 'app-clinical-event-list',
  templateUrl: './clinical-event-list.component.html',
  styleUrls: ['./clinical-event-list.component.scss']
})
export class ClinicalEventListComponent implements OnInit, OnDestroy
{
  public pageSizes = [5, 10, 25, 50, 100];
  public enableAdvanced = false;
  public currentUser = this.authenticationService.currentUser;
  public breadcrumb = 'ALL';
  public stringLiteralBreadcrumb = false;
  public filterDialogOpened = false;
  public assigneeDialogOpened = false;

  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public filterElement: any;

  public isUpdate = false;

  /**
   * Case list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public filterText = '';

  public patientLabel = {};
  /**
   * Subscription to Angular component route parameters.
   */
  public routeParamSubscription: Subscription;

  public eventTypes: Array<{ text: string; value: string; icon: string }>;
  public encounterTypes: Array<{ text: string; value: string }>;

  private listSubscription: Subscription;

  constructor(
    private filterService: FilterService,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private eventListService: EventListService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private settingService: SettingService,
    public intl: IntlService)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.eventListService.loading;
  }

  public ngOnInit(): void
  {
    this.eventTypes = this.eventListService.eventTypes;
    this.encounterTypes = this.eventListService.encounterTypes;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.listSubscription = this.eventListService.subscribe({
      next: (value) =>
      {
        // Preprocessing data before it's passed to the Kendo Grid.
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.eventDateTime = new Date(d.eventDateTime);
            d.eventTypeIcon = this.eventListService.getEventTypeIcon(d.eventType);
            d.eventTypeText = this.eventListService.getEventTypeText(d.eventType);
            d.encounterTypeText = this.eventListService.getEncounterTypeText(d.encounterType);
          });
        }

        // TODO - this interaction between the component and the data service can be simplified / clean up.
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.total = value.total : this.total = 0
        };
      }
    });

    // fetch events
    this.routeParamSubscription = this.route.params.subscribe({
      next: (params) =>
      {
        this.getEvents(params.filter);
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.eventListService.queryByStateAndSource(this.state, 'eventList');
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, 'details');
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.eventListService.queryByStateAndSource(this.state, 'eventList');
  }

  public getEventIconTitle(dataItem: EventDetails): string
  {
    const eventTypeText = dataItem?.eventTypeText;
    const encounterTypeText = dataItem?.encounterTypeText;
    const encounterText = encounterTypeText !== null ? `(${encounterTypeText})` : '';
    const appointmentStatusText = dataItem?.appointment && dataItem?.appointment.statusText ? `(${dataItem?.appointment.statusText})` : '';

    return `${eventTypeText} ${encounterText} ${appointmentStatusText}`;
  }
  /**
   * Get cases from API per the applied filter.
   *
   * @param filter Case filter string.
   */
  private getEvents(filter: string): void
  {
    if (filter === 'all')
    {
      this.state = {
        filter: {
          filters: [],
          logic: 'and'
        },
        group: [],
        skip: 0,
        sort: [],
        take: this.appConfigService.getConfig().pageSize
      };
      this.breadcrumb = 'ALL';
      this.stringLiteralBreadcrumb = false;

      this.eventListService.queryByStateAndSource(this.state, 'eventList');
      this.isUpdate = false;
      this.filterElement = undefined;
    }
    else
    {
      this.filterService.getCachedEventFilters(this.currentUser.userName).subscribe({
        next: (filterList) =>
        {
          const item = filterList.find(obj => obj.id.toString() === filter.toString());
          this.filterElement = item;
          this.breadcrumb = item.name;
          this.stringLiteralBreadcrumb = true;

          this.state = JSON.parse(item.state);
          // reset pagination numbers as we're more concerned with filters and sorting.
          if (this.state === null)
          {
            this.state = {};
          }
          this.state.skip = 0;
          this.state.take = this.appConfigService.getConfig().pageSize;

          this.eventListService.queryByStateAndSource(this.state, 'eventList');
        }
      });
      this.isUpdate = true;
    }
  }
}
